// Utils/IndexedDBUtils.js
const DB_NAME = 'SahiCRMIDB';
const DB_VERSION = 1; // This should match your indxDBVer variable

// Initialize IndexedDB
export const initIndexedDB = () => {
  return new Promise((resolve, reject) => {
    if (!window.indexedDB) {
      console.log("Your browser doesn't support a stable version of IndexedDB.");
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      console.error('IndexedDB error:', request.error);
      reject(request.error);
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains('Conns')) {
        db.createObjectStore('Conns', { keyPath: 'NodeID' });
      }

      if (!db.objectStoreNames.contains('Threads')) {
        db.createObjectStore('Threads', { keyPath: 'NodeID' });
      }

      if (!db.objectStoreNames.contains('Contracts')) {
        db.createObjectStore('Contracts', { keyPath: 'NodeID' });
      }

      if (!db.objectStoreNames.contains('Tasks')) {
        db.createObjectStore('Tasks', { keyPath: 'TaskID' }); // ✓ Use TaskID
      }
    };
  });
};

/**
 * Get a specific thread by ThreadID from the Threads store
 * @param {string|number} threadID - The thread ID to fetch
 * @returns {Promise<Object|null>} Promise that resolves to the thread object or null if not found
 */
export const getThreadByID = async (threadID) => {
  try {
    const db = await initIndexedDB();

    if (!db.objectStoreNames.contains('Threads')) {
      console.warn('Threads store does not exist');
      db.close();
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Threads'], 'readonly');
      const threadsStore = transaction.objectStore('Threads');
      const request = threadsStore.getAll();

      request.onsuccess = () => {
        const threads = request.result || [];

        // Find thread with matching ThreadID
        const thread = threads.find(t =>
          t.ThreadID === threadID ||
          t.ThreadID === String(threadID) ||
          t.ThreadID === Number(threadID)
        );

        db.close();
        resolve(thread || null);
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });

  } catch (error) {
    console.error('Error fetching thread by ID:', error);
    return null;
  }
};

/**
 * Get a specific connection by ConnID from the Conns store
 * @param {string|number} connID - The connection ID to fetch
 * @returns {Promise<Object|null>} Promise that resolves to the connection object or null if not found
 */
export const getConnectionByID = async (nodeID) => {
  try {
    const db = await initIndexedDB();

    if (!db.objectStoreNames.contains('Conns')) {
      console.warn('Contacts store does not exist');
      db.close();
      return null;
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Conns'], 'readonly');
      const connectionStore = transaction.objectStore('Conns');
      const getRequest = connectionStore.get(Number(nodeID));

      getRequest.onsuccess = (event) => {
        let connectionDetail = event.target.result || [];
        db.close();
        resolve(connectionDetail || null);
      };

      getRequest.onerror = (event) => {
        db.close();
        reject(event.target.errorCode);
      };
    });

  } catch (error) {
    console.error('Error fetching thread by ID:', error);
    return null;
  }
}

/**
 * Get threads by connection ID using OpenThreadIDs
 * @param {string|number} connID - The connection ID to fetch threads for
 * @returns {Promise<Array>} Promise that resolves to array of thread objects
 */
export const getThreadsByConnID = async (connID) => {
  try {
    // First get the connection to access OpenThreadIDs
    const connection = await getConnectionByID(connID);

    if (!connection || !connection.OpenThreadIDs) {
      return [];
    }

    const openThreadIDsArr = connection.OpenThreadIDs.split(",").map(id => id.trim()).filter(id => id);

    if (openThreadIDsArr.length === 0) {
      return [];
    }

    const db = await initIndexedDB();

    if (!db.objectStoreNames.contains('Threads')) {
      console.warn('Threads store does not exist');
      db.close();
      return [];
    }

    return new Promise((resolve, reject) => {
      const transaction = db.transaction(['Threads'], 'readonly');
      const threadsStore = transaction.objectStore('Threads');
      const request = threadsStore.getAll();

      request.onsuccess = () => {
        const allThreads = request.result || [];

        // Filter threads that match the OpenThreadIDs
        const matchedThreads = allThreads.filter(thread =>
          openThreadIDsArr.includes(String(thread.ThreadID))
        );

        db.close();
        resolve(matchedThreads);
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };
    });

  } catch (error) {
    console.error('Error fetching threads by connection ID:', error);
    return [];
  }
};

// Get all data from a specific store
export const getFromIndexedDB = (storeName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        resolve([]);
        return;
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        db.close();
        resolve(request.result || []);
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Set/Insert data into IndexedDB store
export const setInIndexedDB = (storeName, dataArray) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      // Add all items
      for (const item of dataArray) {
        store.put(item);
      }

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Update data in IndexedDB store
export const updateIndexedDB = (storeName, dataArray) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      // Update all items
      for (const item of dataArray) {
        store.put(item);
      }

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Remove specific items from IndexedDB store
export const removeFromIndexedDB = (storeName, dataArray) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);

      // Delete items by appropriate key
      for (const item of dataArray) {
        const keyToDelete = storeName === 'Tasks' ? item.TaskID : item.NodeID;
        if (keyToDelete) {
          store.delete(keyToDelete);
        }
      }

      transaction.oncomplete = () => {
        db.close();
        resolve();
      };

      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Clear entire store
export const clearIndexedDB = (storeName) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.clear();

      request.onsuccess = () => {
        console.log(`IndexedDB store ${storeName} is cleared.`);
        db.close();
        resolve();
      };

      request.onerror = () => {
        console.error('Error clearing IndexedDB store:', request.error);
        db.close();
        reject(request.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

export const resetEverything = () => {
  localStorage.clear();
  window.indexedDB.deleteDatabase('SahiCRMIDB');
}

// Get a specific value by key
export const getIndexedDBValue = (storeName, key) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve(null);
        return;
      }

      const transaction = db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(Number(key));

      request.onsuccess = () => {
        db.close();
        resolve(request.result);
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Clear data for a specific agency in a store
export const clearAgencyDataInIndexedDB = (storeName, agencyID) => {
  return new Promise(async (resolve, reject) => {
    try {
      const db = await initIndexedDB();

      if (!db.objectStoreNames.contains(storeName)) {
        console.warn(`Store ${storeName} does not exist`);
        db.close();
        resolve();
        return;
      }

      const transaction = db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();

      request.onsuccess = () => {
        const items = request.result || [];
        let deletedCount = 0;

        items.forEach(item => {
          let shouldDelete = false;
          if (storeName === 'Contracts') {
            shouldDelete = Number(item.BuyerAgencyID) === Number(agencyID) || Number(item.SellerAgencyID) === Number(agencyID);
          } else {
            shouldDelete = Number(item.AgencyID) === Number(agencyID);
          }

          if (shouldDelete) {
            const key = storeName === 'Tasks' ? item.TaskID : item.NodeID;
            if (key) {
              store.delete(key);
              deletedCount++;
            }
          }
        });

        transaction.oncomplete = () => {
          db.close();
          resolve();
        };
      };

      request.onerror = () => {
        db.close();
        reject(request.error);
      };

    } catch (error) {
      reject(error);
    }
  });
};

// Delete entire database
export const deleteIndexedDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DB_NAME);

    request.onsuccess = () => {
      console.log("Deleted database successfully");
      resolve();
    };

    request.onerror = () => {
      console.log("Couldn't delete database");
      reject(request.error);
    };

    request.onblocked = () => {
      console.log("Couldn't delete database due to the operation being blocked");
      reject(new Error('Database deletion blocked'));
    };
  });
};